import { useEffect, useState } from "react";
import useSWR from "swr";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fetcher } from "../../lib/api";
import { PageHeader, Panel, Field, GoldButton } from "../../components/admin/AdminUI";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import ImageUpload from "../../components/admin/ImageUpload";

function PasswordPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("A nova senha deve ter ao menos 8 caracteres.");
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Senha alterada");
      setCur("");
      setNext("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Panel title="Senha do administrador">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3 sm:items-end">
        <Field label="Senha atual"><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-current-password" /></Field>
        <Field label="Nova senha"><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="settings-new-password" /></Field>
        <GoldButton type="submit" data-testid="settings-change-password">Alterar senha</GoldButton>
      </form>
    </Panel>
  );
}

export default function Settings() {
  const { data, mutate } = useSWR("/admin/settings", fetcher);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setF(data);
  }, [data]);

  if (!f) return <p className="text-sm text-[#A1A1AA]">Carregando…</p>;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const input = (k, label, props = {}) => <Field label={label} hint={props.hint}><Input value={f[k] || ""} onChange={set(k)} placeholder={props.placeholder} data-testid={`settings-${k}`} /></Field>;
  const area = (k, label, rows = 4) => <Field label={label} className="sm:col-span-2"><Textarea rows={rows} value={f[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} /></Field>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { ...f, banner_interval: Number(f.banner_interval) });
      toast.success("Configurações salvas — o site já foi atualizado");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-24" data-testid="admin-settings">
      <PageHeader title="Configurações" subtitle="Identidade, textos, contato e redes sociais" />
      <Panel title="Identidade visual">
        <div className="grid gap-6 sm:grid-cols-3">
          <Field label="Logomarca" hint="PNG com fundo transparente recomendado"><ImageUpload value={f.logo} onChange={set("logo")} testid="settings-logo" /></Field>
          <Field label="Fundo da tela de boas-vindas"><ImageUpload value={f.welcome_bg} onChange={set("welcome_bg")} testid="settings-welcome-bg" aspect="aspect-[4/5]" /></Field>
          <Field label="Imagem inicial (sem banners)" hint="Usada quando nenhum banner está publicado"><ImageUpload value={f.hero_image} onChange={set("hero_image")} testid="settings-hero-image" aspect="aspect-video" /></Field>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">{input("store_name", "Nome da loja")}</div>
      </Panel>
      <Panel title="WhatsApp">
        <div className="grid gap-5 sm:grid-cols-2">{input("whatsapp", "Número que recebe os pedidos", { hint: "Com DDD. Ex.: (11) 93067-1750" })}</div>
      </Panel>
      <Panel title="Textos institucionais">
        <div className="grid gap-5 sm:grid-cols-2">
          {input("welcome_title", "Título da tela de boas-vindas")}
          {input("hero_title", "Título da imagem inicial")}
          {area("welcome_text", "Texto da tela de boas-vindas", 2)}
          {area("hero_subtitle", "Subtítulo da imagem inicial", 2)}
          {area("about_text", "Página Sobre", 6)}
          {area("privacy_text", "Política de privacidade", 6)}
        </div>
      </Panel>
      <Panel title="Contato">
        <div className="grid gap-5 sm:grid-cols-2">
          {input("contact_phone", "Telefone")}
          {input("contact_email", "E-mail")}
          {input("address", "Endereço")}
          {input("business_hours", "Horário de atendimento")}
        </div>
      </Panel>
      <Panel title="Redes sociais">
        <div className="grid gap-5 sm:grid-cols-2">
          {input("instagram", "Instagram", { placeholder: "https://instagram.com/..." })}
          {input("facebook", "Facebook", { placeholder: "https://facebook.com/..." })}
          {input("tiktok", "TikTok", { placeholder: "https://tiktok.com/@..." })}
          {input("youtube", "YouTube", { placeholder: "https://youtube.com/..." })}
        </div>
      </Panel>
      <PasswordPanel />
      <div className="fixed bottom-0 left-0 right-0 z-20 flex justify-end border-t border-[#D4AF37]/15 bg-[#0B0B0C]/90 px-5 py-4 backdrop-blur lg:left-64 lg:px-10">
        <GoldButton onClick={save} disabled={saving} data-testid="settings-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Salvar configurações</GoldButton>
      </div>
    </div>
  );
}
